'use client';

import { useTranslations } from 'next-intl';
import { H2 } from './styleConstants';
import { useIsomorphicLayoutEffect } from 'hooks/dom/useIsomorphicLayoutEffect';
import { useRef } from 'react';
import gsap from 'gsap';
import { useGsapContext } from 'hooks/animation/useGsapContext';
import { Container, CustomImage } from '..';
import { getCompressedAssetUrl } from 'lib/config/assetUrls';
import { drawImageSafe } from 'utils/drawImageSafe';
import { useWindowSize } from 'hooks/dom/useWindowSize';
import { BookTestCta } from './BookTestCta';
import { useBloodVisionBooking } from './hooks/useBloodVisionBooking';
import styles from './HowItWorkSection.module.css';

export const HowItWorkSection = ({ className }: { className?: string }) => {
  const { width } = useWindowSize();
  const t = useTranslations('bloodVision');
  const booking = useBloodVisionBooking();

  const resolutionMap = {
    sm: {
      tag: 'w_600',
      width: 600,
      height: 600,
      dir: 'sm',
      start: 2,
      frameStep: 1,
    },
    lg: {
      tag: 'w_1080',
      width: 1080,
      height: 1080,
      dir: 'lg',
      start: 1,
      frameStep: 1,
    },
  };

  const intersectionObserver = useRef<IntersectionObserver | null>(null);
  const gsapContext = useGsapContext();

  const sectionRootRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const tabsContainerRef = useRef<HTMLDivElement>(null);

  const titleRef = useRef<HTMLHeadingElement>(null);

  const step1 = useRef<HTMLDivElement>(null);
  const step2 = useRef<HTMLDivElement>(null);
  const step3 = useRef<HTMLDivElement>(null);

  const btn1 = useRef<HTMLButtonElement>(null);
  const btn2 = useRef<HTMLButtonElement>(null);
  const btn3 = useRef<HTMLButtonElement>(null);

  const miniProgress1 = useRef<HTMLSpanElement>(null);
  const miniProgress2 = useRef<HTMLSpanElement>(null);

  const step4 = useRef<HTMLDivElement>(null);

  const progressBoxRef = useRef<HTMLDivElement>(null);

  const canvas = useRef<HTMLCanvasElement>(null);

  useIsomorphicLayoutEffect(() => {
    if (
      !sectionRootRef.current ||
      !containerRef.current ||
      !step1.current ||
      !step2.current ||
      !step3.current ||
      !step4.current ||
      !canvas.current ||
      !tabsContainerRef.current ||
      !progressBoxRef.current ||
      !titleRef.current ||
      !miniProgress1.current ||
      !miniProgress2.current ||
      !btn1.current ||
      !btn2.current ||
      !btn3.current
    )
      return;

    const canvasEl = canvas.current;
    const context = canvasEl.getContext('2d');

    canvasEl.width =
      window.innerWidth > 767 ? resolutionMap.lg.width : resolutionMap.sm.width;
    canvasEl.height =
      window.innerWidth > 767
        ? resolutionMap.lg.height
        : resolutionMap.sm.height;

    const frameCount = 142;

    const currentFrame = (index: number) =>
      getCompressedAssetUrl(
        `/web_v2/sequence/blood_vision-working-step2/${(index + 1)
          .toString()
          .padStart(4, '0')}.png/${
          window.innerWidth > 767 ? resolutionMap.lg.tag : resolutionMap.sm.tag
        }`,
      );

    if (context) {
      const img = new Image();
      img.src = currentFrame(0);
      img.onload = () => {
        drawImageSafe(context, img, 0, 0);
      };
    }

    intersectionObserver.current = new IntersectionObserver(
      () => {
        gsapContext.add(() => {
          // gsap.set(textContent1.current, {
          //   x: '200px'
          // });

          if (context) {
            const img = new Image();
            img.src = currentFrame(0);
            img.onload = () => {
              drawImageSafe(context, img, 0, 0);
            };
          }

          const images: any = [];

          const frames = {
            frame: 0,
          };

          for (
            let i = 0;
            i < frameCount;
            i +=
              window.innerWidth > 767
                ? resolutionMap.lg.frameStep
                : resolutionMap.sm.frameStep
          ) {
            const img = new Image();
            // img.onload = onImageLoad;
            img.src = currentFrame(i);
            images.push(img);
          }

          const render = () => {
            const img = images[frames.frame];
            if (!img?.complete || img.naturalWidth === 0) return;
            if (!context) return;
            requestAnimationFrame(() => {
              context.clearRect(0, 0, canvasEl.width, canvasEl.height);
              drawImageSafe(context, img, 0, 0);
            });
          };

          gsap.set(progressBoxRef.current, {
            autoAlpha: 0,
          });

          const tl = gsap.timeline({
            defaults: {
              duration: 1,
              ease: 'Power2.easeInOut',
            },
            scrollTrigger: {
              immediateRender: false,
              trigger: sectionRootRef.current,
              start: 'top top',
              end: 'bottom bottom',
              scrub: 0,
              invalidateOnRefresh: true,
            },
          });

          gsap.set(step2.current, {
            autoAlpha: 0,
          });
          gsap.set(step3.current, {
            autoAlpha: 0,
          });
          gsap.set(step4.current, {
            autoAlpha: 0,
          });
          gsap.set(btn1.current, {
            color: '#4664FF',
          });

          // text content 1
          tl.fromTo(
            step1.current,
            { autoAlpha: 1 },
            { autoAlpha: 0, display: 'none', duration: '0.1' },
            '0.1',
          );
          tl.to(btn2.current, { color: '#4664FF', duration: '0.05' }, '0.25');

          // text content 2
          tl.to(step2.current, { autoAlpha: 1, duration: '0.1' }, '0.2');
          tl.to(
            miniProgress1.current,
            { backgroundPositionX: 0, duration: '0.1' },
            '0.2',
          );
          tl.to(btn3.current, { color: '#4664FF', duration: '0.05' }, '0.55');

          tl.fromTo(
            frames,
            {
              frame: 0,
            },
            {
              frame: images.length - 1,
              snap: 'frame',
              ease: 'ease-in',
              onUpdate: render, // use animation onUpdate instead of scrollTrigger's onUpdate
              duration: 0.2,
            },
            '0.25',
          );

          tl.to(step2.current, { autoAlpha: 0, duration: '0.1' }, '0.5');
          tl.to(
            miniProgress2.current,
            { backgroundPositionX: 0, duration: '0.1' },
            '0.5',
          );

          // text content 3
          tl.to(step3.current, { autoAlpha: 1, duration: '0.1' }, '0.6');
          tl.to(step3.current, { autoAlpha: 0, duration: '0.1' }, '0.7');

          // tabs container
          tl.to(tabsContainerRef.current, { y: `40vh`, duration: 0.05 }, '0.8');

          // progress box
          tl.to(
            progressBoxRef.current,
            { autoAlpha: 1, duration: 0.05 },
            '0.8',
          );

          const gradient = {
            degree: 0,
          };
          const updateGradient = () => {
            if (!progressBoxRef.current) return;
            progressBoxRef.current.style.background = `conic-gradient(from ${gradient.degree}deg, rgba(70, 100, 255, 1) 0%, rgba(255, 255, 255, 0) 65%)`;
          };

          // title ref
          tl.to(
            titleRef.current,
            {
              autoAlpha: 0,
              duration: 0.05,
            },
            0.8,
          );
          tl.to(
            [miniProgress1.current, miniProgress2.current],
            {
              autoAlpha: 0,
              duration: 0.05,
            },
            0.8,
          );

          tl.fromTo(
            gradient,
            {
              degree: 80,
            },
            {
              degree: -470,
              onUpdate: updateGradient,
              duration: 0.3,
            },
            '0.8',
          );

          // tabs container side more down
          tl.to(tabsContainerRef.current, { y: `40vh`, duration: 0.05 }, '1.0');

          // step 4
          tl.fromTo(
            step4.current,
            {
              autoAlpha: 0,
              // y: '-40px',
              xPercent: -50,
            },
            {
              autoAlpha: 1,
              y: '0',
              duration: 0.1,
              // xPercent: -50
            },
            '1.0',
          );

          // end delay
          tl.delay(0.5);
        });
      },
      {
        root: null,
        rootMargin: '20px',
        threshold: 0.01,
      },
    );

    intersectionObserver.current.observe(containerRef.current);

    return () => {
      intersectionObserver.current?.disconnect();
      gsapContext.revert();
    };
  }, [width]);

  return (
    <section
      className={className ? `${styles.root} ${className}` : styles.root}
      ref={sectionRootRef}
    >
      <div className={styles.stickyWrapper} ref={containerRef}>
        <Container className={styles.sectionContent}>
          <H2 ref={titleRef}>{t('howItWorks.title')}</H2>
          <div className={styles.tabsContainer} ref={tabsContainerRef}>
            <div className={styles.progressLine} ref={progressBoxRef}></div>
            <button ref={btn1}>
              {t('howItWorks.tabs.bloodTest')}
              <i className={styles.progressShortLine} ref={miniProgress1}></i>
            </button>
            <button ref={btn2}>
              {t.rich('howItWorks.tabs.receiveData', {
                desktop: (chunks) => (
                  <span className={styles.dMd}>{chunks}</span>
                ),
              })}
              <i className={styles.progressShortLine} ref={miniProgress2}></i>
            </button>
            <button ref={btn3}>
              {t.rich('howItWorks.tabs.personaliseRoadmap', {
                desktop: (chunks) => (
                  <span className={styles.dMd}>{chunks}</span>
                ),
              })}
            </button>
          </div>
          <div className={styles.stepContainerWrapper}>
            <div
              className={styles.stepContainer}
              ref={step1}
              style={{ textAlign: 'center' }}
            >
              <div className={styles.step1Img}>
                <CustomImage
                  src="web_v2/blood-vision/blood-marker-graph.png"
                  alt=""
                  fill
                  // className={styles.step1Img}
                />
              </div>
              <div className={styles.textContainer}>
                <p className={styles.stepCount}>
                  {t('howItWorks.stepCount', { step: 1 })}
                </p>
                <h4>{t('howItWorks.step1.title')}</h4>
                <p className={styles.description}>
                  {t('howItWorks.step1.description')}
                </p>
              </div>
            </div>

            <div className={styles.stepContainer} ref={step2}>
              <canvas
                ref={canvas}
                className={styles.sequenceAnimation}
              ></canvas>
              <div className={styles.textContainer}>
                <p className={styles.stepCount}>
                  {t('howItWorks.stepCount', { step: 2 })}
                </p>
                <h4>{t('howItWorks.step2.title')}</h4>
                <p className={styles.description}>
                  {t('howItWorks.step2.description')}
                </p>
              </div>
            </div>

            <div className={styles.stepContainer} ref={step3}>
              <div className={styles.phoneImageContainer}>
                <CustomImage
                  src="web_v2/blood-vision/bv-phone-ss-4x.png"
                  fill
                  alt={t('howItWorks.step3.imageAlt')}
                />
              </div>
              <div className={styles.textContainer}>
                <p className={styles.stepCount}>
                  {t('howItWorks.stepCount', { step: 3 })}
                </p>
                <h4>{t('howItWorks.step3.title')}</h4>
                <p className={styles.description}>
                  {t('howItWorks.step3.description')}
                </p>
              </div>
            </div>
          </div>
          <div className={styles.stepContainerExpanded} ref={step4}>
            <div className={styles.textContainer}>
              {/* <p className={styles.stepCount}></p> */}
              <h4>{t('howItWorks.step4.title')}</h4>
              <p className={styles.description}>
                {t('howItWorks.step4.description')}
              </p>
            </div>

            {booking ? <BookTestCta className={styles.bookCta} /> : null}
          </div>
        </Container>
      </div>
    </section>
  );
};
